import * as React from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import {
    useFetchUserDetails,
    useFetchUserPractices,
} from "./modules/auth/hooks/auth.queries";
import SwitchProfile from "./components/SwitchProfile/SwitchProfile";
import PracticeSelection from "./components/PracticeSelection/PracticeSelection";
import AppRoutes from "./AppRoutes";
import { useAppDispatch } from "./hooks/useRedux";
import { handleChangePractice } from "./redux/features/practice/practiceSlice";
import { fetchUserPractices } from "./modules/auth/services/auth.services";
import { IPractice } from "./modules/auth/types/auth.types";
import axiosInstance from "./utils/axios";
import useToast from "./hooks/useToast";
import { getClientSession, setClientSession } from "./utils/clientSessionStore";

export default function App() {
    const location = useLocation();
    const isOtpVerificationRoute =
        location.pathname.includes("/otp-verification");
    const isConsentLinkRoute = location.pathname === "/consent-link";

    const {
        data,
        isSuccess,
        isError: isUserDetailsError,
        isLoading: isUserDetailsLoading,
        isFetching: isUserDetailsFetching,
    } = useFetchUserDetails({
        enabled: !isOtpVerificationRoute && !isConsentLinkRoute,
    });

    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const { toast } = useToast();

    const userId = data?.data?.user_id ?? "";
    const subProfiles = data?.data?.sub_profiles ?? [];
    const {
        data: userPractices,
        isFetching: isPracticesFetching,
        isLoading: isPracticesLoading,
        isError: isPracticesError,
    } = useFetchUserPractices(userId);

    const { clientId } = getClientSession();

    const practices = userPractices?.data?.practices;
    const defaultPractice =
        practices && practices.length === 1 ? practices[0] : null;

    const isSingleProfileUser = isSuccess && subProfiles.length === 0;
    const isMultiProfileUser = isSuccess && subProfiles.length > 0;
    const shouldResolvePractices = isSingleProfileUser && !clientId;
    const shouldAutoSelectDefaultPractice =
        shouldResolvePractices && Boolean(defaultPractice);

    const isUserDetailsPending = isUserDetailsLoading && isUserDetailsFetching;
    const isPracticesPending = isPracticesLoading && isPracticesFetching;

    const lastProcessedPaymentLink = React.useRef<{
        practiceId: string;
        userId: string;
    } | null>(null);
    const isProcessingPaymentRedirect = React.useRef(false);
    const [isRedirectingToPayment, setIsRedirectingToPayment] =
        React.useState(false);

    React.useEffect(() => {
        const searchParams = new URLSearchParams(location.search);
        const practiceId = searchParams.get("practiceId");
        const urlUserId = searchParams.get("userId");

        if (practiceId && urlUserId) {
            const lastProcessed = lastProcessedPaymentLink.current;
            const isNewPaymentLink =
                !lastProcessed ||
                lastProcessed.practiceId !== practiceId ||
                lastProcessed.userId !== urlUserId;

            if (isNewPaymentLink) {
                const cleanSearchParams = new URLSearchParams(location.search);
                cleanSearchParams.delete("practiceId");
                cleanSearchParams.delete("userId");

                const cleanSearch = cleanSearchParams.toString();
                const cleanUrl = `${location.pathname}${cleanSearch ? `?${cleanSearch}` : ""}`;

                localStorage.setItem("pendingPaymentUrl", cleanUrl);
                localStorage.setItem("pendingPracticeId", practiceId);
                localStorage.setItem("pendingUserId", urlUserId);
                lastProcessedPaymentLink.current = {
                    practiceId,
                    userId: urlUserId,
                };

                window.history.replaceState(window.history.state, "", cleanUrl);
            }
        } else {
            lastProcessedPaymentLink.current = null;
        }
    }, [location, navigate]);

    React.useEffect(() => {
        const pendingPaymentUrl = localStorage.getItem("pendingPaymentUrl");
        const pendingPracticeId = localStorage.getItem("pendingPracticeId");
        const pendingUserId = localStorage.getItem("pendingUserId");

        if (!pendingPaymentUrl || !pendingPracticeId || !pendingUserId) {
            return;
        }

        if (isProcessingPaymentRedirect.current) {
            return;
        }

        if (!isSuccess || !data?.data) {
            return;
        }

        const authenticatedUserId = data.data.user_id;
        const subProfilesList = data.data.sub_profiles ?? [];

        const isValidUserId =
            pendingUserId === authenticatedUserId ||
            subProfilesList.some(
                (profile) => profile.user_id === pendingUserId
            );

        if (!isValidUserId) {
            localStorage.removeItem("pendingPaymentUrl");
            localStorage.removeItem("pendingPracticeId");
            localStorage.removeItem("pendingUserId");
            toast({
                mode: "error",
                message:
                    "This payment link is associated with a different account. Please log in with the correct account.",
                duration: 5000,
            });
            return;
        }

        const {
            practice: currentPractice,
            clientId: currentClientId,
            userId: currentUserId,
        } = getClientSession();

        if (
            currentPractice &&
            currentClientId &&
            currentUserId &&
            currentPractice.practice_id === pendingPracticeId &&
            currentUserId === pendingUserId
        ) {
            localStorage.removeItem("pendingPaymentUrl");
            localStorage.removeItem("pendingPracticeId");
            localStorage.removeItem("pendingUserId");
            navigate(pendingPaymentUrl, { replace: true });
            return;
        }

        (async () => {
            if (isProcessingPaymentRedirect.current) {
                return;
            }

            isProcessingPaymentRedirect.current = true;
            setIsRedirectingToPayment(true);

            try {
                const practicesData = await fetchUserPractices(pendingUserId);
                const userPracticesList = practicesData?.data?.practices ?? [];

                const targetPractice = userPracticesList.find(
                    (practice: IPractice) =>
                        practice.practice_id === pendingPracticeId
                );

                if (!targetPractice) {
                    localStorage.removeItem("pendingPaymentUrl");
                    localStorage.removeItem("pendingPracticeId");
                    localStorage.removeItem("pendingUserId");
                    isProcessingPaymentRedirect.current = false;
                    setIsRedirectingToPayment(false);
                    toast({
                        mode: "error",
                        message:
                            "This payment link is invalid or you don't have access to the associated practice.",
                        duration: 5000,
                    });
                    return;
                }

                axiosInstance.defaults.headers.common.practice_id =
                    targetPractice.practice_id;
                axiosInstance.defaults.headers.common.client_id =
                    targetPractice.client_id;

                setClientSession({
                    clientId: targetPractice.client_id,
                    userId: pendingUserId,
                    practice: targetPractice,
                });

                dispatch(handleChangePractice({ practice: targetPractice }));

                queryClient.removeQueries();

                localStorage.removeItem("pendingPaymentUrl");
                localStorage.removeItem("pendingPracticeId");
                localStorage.removeItem("pendingUserId");

                isProcessingPaymentRedirect.current = false;
                setIsRedirectingToPayment(false);

                navigate(pendingPaymentUrl, { replace: true });
            } catch (error) {
                localStorage.removeItem("pendingPaymentUrl");
                localStorage.removeItem("pendingPracticeId");
                localStorage.removeItem("pendingUserId");
                isProcessingPaymentRedirect.current = false;
                setIsRedirectingToPayment(false);

                toast({
                    mode: "error",
                    message:
                        "Unable to load payment information. Please try again or select your practice.",
                    duration: 5000,
                });

                navigate("/", { replace: true });
            }
        })();
    }, [isSuccess, data, navigate, dispatch, queryClient, toast, userId]);

    React.useEffect(() => {
        if (!shouldAutoSelectDefaultPractice || !defaultPractice) {
            return;
        }

        if (localStorage.getItem("pendingPracticeId")) {
            return;
        }

        setClientSession({
            clientId: defaultPractice.client_id,
            userId,
            practice: defaultPractice,
        });

        dispatch(handleChangePractice({ practice: defaultPractice }));
        window.location.href = "/";
    }, [defaultPractice, dispatch, shouldAutoSelectDefaultPractice, userId]);

    if (isRedirectingToPayment) {
        return (
            <div className="page-loader">
                <span />
            </div>
        );
    }

    if (isUserDetailsPending) {
        return (
            <div className="page-loader">
                <span />
            </div>
        );
    }

    if (isUserDetailsError) {
        return <AppRoutes />;
    }

    if (isSuccess) {
        if (shouldResolvePractices) {
            if (isPracticesPending) {
                return (
                    <div className="page-loader">
                        <span />
                    </div>
                );
            }

            if (isPracticesError) {
                return <Navigate to="/login" replace />;
            }

            if (practices) {
                if (practices.length === 0) {
                    return <Navigate to="/login" replace />;
                }

                if (
                    practices.length > 1 &&
                    !localStorage.getItem("pendingPracticeId")
                ) {
                    return <PracticeSelection />;
                }
            }

            if (shouldAutoSelectDefaultPractice) {
                return (
                    <div className="page-loader">
                        <span />
                    </div>
                );
            }

            if (userId && !practices && !isPracticesError) {
                return (
                    <div className="page-loader">
                        <span />
                    </div>
                );
            }

            return <AppRoutes />;
        }

        if (!clientId && isMultiProfileUser) {
            return <SwitchProfile />;
        }

        return <AppRoutes />;
    }

    return <AppRoutes />;
}
